import { useState, useEffect, useRef } from "react";

import { Button, Section, Field, Status } from "./component.js";
import {
  US_STATES,
  CLAIM_PAYERS,
  normalizeState,
  DEFAULT_PLACE_OF_SERVICE,
  PLACE_OF_SERVICE_OPTIONS,
} from "../../utils/constants.js";
import { sanitizeMBI, isValidMBI } from "../../utils/mbi.js";
import { sanitizeZip, isValidZip, ZIP_HINT } from "../../utils/zip.js";

const STATE_OPTIONS = ["", ...US_STATES];

const REQUIRED_FIELDS = [
  "payer",
  "mbi",
  "patientLastName",
  "patientFirstName",
  "patientDOB",
  "patientSex",
  "patientAddress",
  "patientCity",
  "patientState",
  "patientZip",
  "primaryDiagnosis",
  "dateOfService",
  "placeOfService",
  "procedureCode",
  "charges",
  "renderingProviderFirstName",
  "renderingProviderLastName",
  "renderingProviderNPI",
  "federalTaxID",
  "facilityName",
  "facilityAddress",
  "facilityCity",
  "facilityState",
  "facilityZip",
  "facilityNPI",
  "billingProviderName",
  "billingProviderAddress",
  "billingProviderCity",
  "billingProviderState",
  "billingProviderZip",
  "billingProviderPhone",
  "billingProviderNPI",
];

const ZIP_FIELDS = ["patientZip", "insuredZip", "facilityZip", "billingProviderZip"];

const payerLabel = (p) => `${p.displayName} - ${p.primaryPayerId}`;
const PAYER_OPTIONS = ["", ...CLAIM_PAYERS.map(payerLabel)];

function defaultPayer(state) {
  const match = CLAIM_PAYERS.find((p) => p.state === state);
  return match ? payerLabel(match) : "";
}

function amountForGCode(claimAmounts, procedureCode) {
  if (!procedureCode || !Array.isArray(claimAmounts) || claimAmounts.length === 0) {
    return "";
  }
  const code = String(procedureCode).trim().toUpperCase();
  const match = claimAmounts.find((a) => String(a.gCode || "").trim().toUpperCase() === code);
  if (!match || match.amount === undefined || match.amount === null || match.amount === "") {
    return "";
  }
  const parsed = parseFloat(match.amount);
  return Number.isFinite(parsed) ? parsed.toFixed(2) : "";
}

export function buildInitialForm(claim, orgSettings) {
  const ci = claim.claimInfo || {};
  return {
    payer: ci.payer || defaultPayer(normalizeState(claim.state)),
    claimIdentifier: ci.claimIdentifier || "CH",
    insuranceType: ci.insuranceType || "Medicare",
    paymentResponsibility: ci.paymentResponsibility || "P",
    mbi: sanitizeMBI(claim.mbi || ""),

    patientLastName: claim.lastName || "",
    patientFirstName: claim.firstName || "",
    patientMiddleName: claim.middleName || "",
    patientDOB: toDateInput(claim.dob),
    patientSex: claim.sex || "Female",
    patientAddress: claim.address || "",
    patientCity: claim.city || "",
    patientState: normalizeState(claim.state),
    patientZip: ci.patientZip || claim.zip || "",
    relationshipToInsured: ci.relationshipToInsured || "Self",

    insuredLastName: ci.insuredLastName || claim.lastName || "",
    insuredFirstName: ci.insuredFirstName || claim.firstName || "",
    insuredMiddleName: ci.insuredMiddleName || claim.middleName || "",
    insuredAddress: ci.insuredAddress || claim.address || "",
    insuredCity: ci.insuredCity || claim.city || "",
    insuredState: ci.insuredState || normalizeState(claim.state),
    insuredZip: ci.insuredZip || claim.zip || "",
    insuredDOB: toDateInput(ci.insuredDOB || claim.dob),
    insuredSex: ci.insuredSex || claim.sex || "Female",

    conditionEmployment: ci.conditionEmployment || "No",
    conditionAuto: ci.conditionAuto || "No",
    conditionOther: ci.conditionOther || "No",
    policyGroup: ci.policyGroup || "",
    patientSignature: ci.patientSignature || "Yes",
    insuredSignature: ci.insuredSignature || "Yes",

    referringProviderName: ci.referringProviderName || claim.referringProviderName || "",
    referringProviderNPI: ci.referringProviderNPI || claim.referringProviderNPI || "",

    primaryDiagnosis: ci.primaryDiagnosis || "",
    secondaryDiagnosis: ci.secondaryDiagnosis || "",

    dateOfService: claim.monthlyCheckinDate || toDateInput(ci.dateOfService) || "",
    placeOfService: ci.placeOfService || orgSettings?.defaultPlaceOfService || DEFAULT_PLACE_OF_SERVICE,
    procedureCode: claim.procedureCode || "",
    diagnosisPointer: ci.diagnosisPointer || "1",
    charges: ci.charges || amountForGCode(orgSettings?.claimAmounts, claim.procedureCode) || "",
    units: ci.units || "1",
    renderingProviderFirstName: claim.renderingProviderFirstName || ci.renderingProviderFirstName || orgSettings?.renderingProviderFirstName || "",
    renderingProviderLastName: claim.renderingProviderLastName || ci.renderingProviderLastName || orgSettings?.renderingProviderLastName || "",
    renderingProviderNPI: claim.renderingProviderNPI || ci.renderingProviderNPI || orgSettings?.renderingProviderNPI || "",

    federalTaxID: orgSettings?.tin ?? "",
    acceptAssignment: ci.acceptAssignment || "Yes",
    physicianSignature: ci.physicianSignature || "Yes",
    billingProviderType: ci.billingProviderType || "Organization",
    billingProviderName: ci.billingProviderName || orgSettings?.title || "",
    billingProviderAddress: ci.billingProviderAddress || orgSettings?.billingProviderAddress || "",
    billingProviderCity: ci.billingProviderCity || orgSettings?.billingProviderCity || "",
    billingProviderState: ci.billingProviderState || orgSettings?.billingProviderState || "",
    billingProviderZip: ci.billingProviderZip || orgSettings?.billingProviderZip || "",
    billingProviderPhone: ci.billingProviderPhone || orgSettings?.billingProviderPhone || "",
    billingProviderNPI: ci.billingProviderNPI || orgSettings?.npi || "",

    facilityName: ci.facilityName || orgSettings?.facilityName || "",
    facilityAddress: ci.facilityAddress || orgSettings?.facilityAddress || "",
    facilityCity: ci.facilityCity || orgSettings?.facilityCity || "",
    facilityState: ci.facilityState || orgSettings?.facilityState || "",
    facilityZip: ci.facilityZip || orgSettings?.facilityZip || "",
    facilityNPI: ci.facilityNPI || orgSettings?.facilityNPI || "",
  };
}

// Normalize stored date values (e.g. ISO "1944-07-15T05:00:00.000Z")
// to the YYYY-MM-DD format required by <input type="date">.
function toDateInput(val) {
  const s = String(val ?? "").trim();
  if (!s) return "";
  const match = s.match(/^\d{4}-\d{2}-\d{2}/);
  return match ? match[0] : "";
}

function normalizeViewForm(form) {
  if (!form) return {};
  return {
    ...form,
    patientDOB: toDateInput(form.patientDOB),
    insuredDOB: toDateInput(form.insuredDOB),
    dateOfService: toDateInput(form.dateOfService),
  };
}

export function getClaimFormIssues(form) {
  const missingCount = REQUIRED_FIELDS.filter((k) => {
    const val = String(form[k] ?? "").trim();
    if (ZIP_FIELDS.includes(k)) return !isValidZip(val);
    return !val;
  }).length;
  const mbiInvalid = !!form.mbi && !isValidMBI(form.mbi);
  const invalidZipCount = ZIP_FIELDS.filter(
    (k) => form[k] && !isValidZip(form[k])
  ).length;
  const isIncomplete = missingCount > 0 || mbiInvalid || invalidZipCount > 0;
  return { missingCount, mbiInvalid, invalidZipCount, isIncomplete };
}

function formatPhone(val) {
  const digits = String(val ?? "").replace(/\D/g, "").slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function formatServiceDate(val) {
  if (!val) return "—";
  const s = String(val).trim().slice(0, 10);
  const d = new Date(s + "T00:00:00");
  if (isNaN(d)) return s;
  return d.toLocaleDateString("en-US", { month: "long", day: "2-digit", year: "numeric" });
}

const INFO_REJECTION_CATEGORY_CODES = new Set(["A6", "A7", "A8"]);

function ClaimAcknowledgment({ ack }) {
  const totalValue = ack.totalClaimValue ? `$${parseFloat(ack.totalClaimValue).toFixed(2)}` : "—";
  const showRejectionReason =
    INFO_REJECTION_CATEGORY_CODES.has(ack.statusCategoryCode) && !!ack.statusDescription;

  return (
    <Section title="Claim Acknowledgment">
      <div className="rounded border border-neutral-200 bg-neutral-100 p-4 mb-3">
        <div className="grid grid-cols-2 gap-x-6 gap-y-3 lg:grid-cols-4">
          <div>
            <div className="typo-normal-125 text-muted mb-1">Patient control number</div>
            <div className="typo-strong-125">{ack.patientControlNumber || "—"}</div>
          </div>
          <div>
            <div className="typo-normal-125 text-muted mb-1">Payer</div>
            <div className="typo-strong-125">{ack.payerName || "—"}</div>
          </div>
          <div>
            <div className="typo-normal-125 text-muted mb-1">Total claim value</div>
            <div className="typo-strong-125">{totalValue}</div>
          </div>
          <div>
            <div className="typo-normal-125 text-muted mb-1">Service date</div>
            <div className="typo-strong-125">{formatServiceDate(ack.serviceDate)}</div>
          </div>
        </div>
      </div>
      <div className="rounded border border-neutral-200 bg-neutral-100 p-4">
        <div className="typo-strong-125 mb-3">Claim info</div>
        <div className="d-flex align-items-start mb-2">
          <span className="typo-normal-125 text-muted me-4" style={{ minWidth: 160 }}>Status category code</span>
          <div className="d-flex align-items-center gap-2">
            <Status color="blue" size="small" muted text={ack.statusCategoryCode || "—"} />
            <span className="typo-normal-125">{ack.statusCategory}</span>
          </div>
        </div>
        <div className="d-flex align-items-start">
          <span className="typo-normal-125 text-muted me-4" style={{ minWidth: 160 }}>Status code</span>
          <div className="d-flex align-items-center gap-2">
            <Status color="grey" size="small" muted text={ack.claimStatusCode || "—"} />
            <span className="typo-strong-125">{ack.statusDescription}</span>
          </div>
        </div>
        {showRejectionReason && (
          <div className="rounded border border-red-300 bg-red-50 p-3 mt-3">
            <div className="typo-strong-150 text-red-700 mb-1">Rejection reason</div>
            <div className="d-flex align-items-center gap-2">
              {ack.claimStatusCode && (
                <Status color="red" size="small" muted text={ack.claimStatusCode} />
              )}
              <span className="typo-normal-150 text-red-700">{ack.statusDescription}</span>
            </div>
          </div>
        )}
      </div>
    </Section>
  );
}

function ClaimFormModal({
  claim,
  submitting,
  onClose,
  onSubmit,
  orgSettings,
  submitErrors,
  viewMode = false,
  viewForm = null,
  initialForm = null,
  resubmitAcknowledgment = null,
  title = null,
  submitLabel = null,
  elevated = false,
  allowIncomplete = false,
  leftIcon = null,
}) {
  const [form, setForm] = useState(() => {
    if (viewMode) return normalizeViewForm(viewForm);
    if (initialForm) return normalizeViewForm(initialForm);
    return buildInitialForm(claim, orgSettings);
  });
  const [showDefaults, setShowDefaults] = useState(viewMode);
  const hideDefaults = !showDefaults;
  const scrollContainerRef = useRef(null);

  function update(key) {
    return (val) => setForm((f) => ({ ...f, [key]: val }));
  }

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  useEffect(() => {
    if (!submitting) return;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [submitting]);

  useEffect(() => {
    if (submitErrors && submitErrors.length > 0 && scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({ top: scrollContainerRef.current.scrollHeight, behavior: 'smooth' });
    }
  }, [submitErrors]);

  useEffect(() => {
    if (viewMode) return;
    setForm((f) => ({
      ...f,
      insuredLastName: f.patientLastName,
      insuredFirstName: f.patientFirstName,
      insuredMiddleName: f.patientMiddleName,
      insuredDOB: f.patientDOB,
      insuredSex: f.patientSex,
      insuredAddress: f.patientAddress,
      insuredCity: f.patientCity,
      insuredState: f.patientState,
      insuredZip: f.patientZip,
    }));
  }, [
    form.patientLastName,
    form.patientFirstName,
    form.patientMiddleName,
    form.patientDOB,
    form.patientSex,
    form.patientAddress,
    form.patientCity,
    form.patientState,
    form.patientZip,
  ]);

  const { missingCount, mbiInvalid, isIncomplete } = getClaimFormIssues(form);

  const headerTitle =
    title ||
    (viewMode
      ? "View Submitted Claim"
      : initialForm
        ? "Resubmit CMS-1500 Claim"
        : "Submit CMS-1500 Claim");
  const actionLabel =
    submitLabel ||
    (submitting ? "Submitting..." : initialForm ? "Resubmit Claim" : "Submit Claim");

  function handleSubmit() {
    onSubmit(form);
  }

  const totalCharges = form.charges
    ? `$${parseFloat(form.charges).toFixed(2)}`
    : "";

  return (
    <div
      className={`fixed inset-0 flex items-center justify-center bg-[#80808094] ${elevated ? "z-2100" : "z-2000"}`}
    >
      <div className="w-full max-w-5xl px-8">
        <div
          className="card shadowmore relative"
          onClick={(e) => e.stopPropagation()}
        >
          {submitting && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-[#80808094]">
              <div className="d-flex flex-column align-items-center">
                <div
                  className="spin-icon"
                  style={{
                    width: 40,
                    height: 40,
                    border: "3px solid #e5e5e5",
                    borderTopColor: "#3b82f6",
                    borderRadius: "50%",
                  }}
                />
                <div className="typo-normal-150 mt-3 text-white">Submitting claim…</div>
              </div>
            </div>
          )}
          <div className="card-header flex items-center justify-between">
            <span className="typo-header-3xLarge">
              {headerTitle}
            </span>
            {!viewMode && (
              <label className="typo-body-medium text-muted flex items-center cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={showDefaults}
                  onChange={(e) => setShowDefaults(e.target.checked)}
                  disabled={submitting}
                  className="me-2"
                />
                Show default (read-only) fields
              </label>
            )}
          </div>
          <div ref={scrollContainerRef} className="card-content max-h-[70vh] overflow-y-auto">
            {viewMode && claim?.acknowledgment && (
              <ClaimAcknowledgment ack={claim.acknowledgment} />
            )}
            {!viewMode && resubmitAcknowledgment && (
              <ClaimAcknowledgment ack={resubmitAcknowledgment} />
            )}
            {(viewMode && claim?.acknowledgment) || (!viewMode && resubmitAcknowledgment) ? (
              <div className="typo-strong-200 mb-3 mt-2 border-b border-neutral-200 pb-2">CMS-1500 Form</div>
            ) : null}
            <Section title="Basic Info">
              <Field
                label="Payer"
                value={form.payer}
                onChange={update("payer")}
                options={PAYER_OPTIONS}
                disabled={viewMode}
                required
              />
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                <Field
                  label="Claim Identifier"
                  value={form.claimIdentifier}
                  disabled
                  hideDefaults={hideDefaults}
                />
                <Field
                  label="Insurance Type"
                  value={form.insuranceType}
                  disabled
                  hideDefaults={hideDefaults}
                />
                <Field
                  label="Payment Responsibility"
                  value={form.paymentResponsibility}
                  disabled
                  hideDefaults={hideDefaults}
                />
              </div>
              <Field
                label="MBI / Insured ID"
                value={form.mbi}
                onChange={(val) => update("mbi")(sanitizeMBI(val))}
                disabled={viewMode}
                required
                error={mbiInvalid}
                hint={mbiInvalid ? "Enter a valid 11-character Medicare Beneficiary Identifier." : undefined}
              />
            </Section>

            <Section title="Patient & Insured">
              <div className="rounded border border-neutral-200 bg-neutral-100 p-6">
                <div className="typo-strong-125 mb-2">Patient Info</div>
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
                  <div className="lg:col-span-5">
                    <Field
                      label="Last Name"
                      value={form.patientLastName}
                      onChange={update("patientLastName")}
                      disabled={viewMode}
                      required
                    />
                  </div>
                  <div className="lg:col-span-5">
                    <Field
                      label="First Name"
                      value={form.patientFirstName}
                      onChange={update("patientFirstName")}
                      disabled={viewMode}
                      required
                    />
                  </div>
                  <div className="lg:col-span-2">
                    <Field
                      label="Middle Name"
                      value={form.patientMiddleName}
                      onChange={update("patientMiddleName")}
                      disabled={viewMode}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                  <Field
                    label="Date of Birth"
                    type="date"
                    value={form.patientDOB}
                    onChange={update("patientDOB")}
                    disabled={viewMode}
                    required
                  />
                  <Field
                    label="Sex"
                    value={form.patientSex}
                    onChange={update("patientSex")}
                    options={["Female", "Male"]}
                    disabled={viewMode}
                    required
                  />
                </div>
                <Field
                  label="Address"
                  value={form.patientAddress}
                  onChange={update("patientAddress")}
                  disabled={viewMode}
                  required
                />
                <div className="grid grid-cols-12 gap-4">
                  <div className="col-span-12 lg:col-span-6">
                    <Field
                      label="City"
                      value={form.patientCity}
                      onChange={update("patientCity")}
                      disabled={viewMode}
                      required
                    />
                  </div>
                  <div className="col-span-6 lg:col-span-3">
                    <Field
                      label="State"
                      value={form.patientState}
                      onChange={update("patientState")}
                      options={STATE_OPTIONS}
                      disabled={viewMode}
                      required
                    />
                  </div>
                  <div className="col-span-6 lg:col-span-3">
                    <Field
                      label="ZIP"
                      value={form.patientZip}
                      onChange={(val) => update("patientZip")(sanitizeZip(val))}
                      disabled={viewMode}
                      required
                      hint={ZIP_HINT}
                      error={!!form.patientZip && !isValidZip(form.patientZip)}
                    />
                  </div>
                </div>
              </div>
              <Field
                label="Relationship to Insured"
                value={form.relationshipToInsured}
                disabled
                hideDefaults={hideDefaults}
              />
              <div className="rounded border border-neutral-200 bg-neutral-100 p-6" hidden={hideDefaults}>
                <div className="typo-strong-125 mb-2">Insured Info</div>
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
                  <div className="lg:col-span-5">
                    <Field label="Last Name" value={form.insuredLastName} disabled />
                  </div>
                  <div className="lg:col-span-5">
                    <Field label="First Name" value={form.insuredFirstName} disabled />
                  </div>
                  <div className="lg:col-span-2">
                    <Field label="Middle" value={form.insuredMiddleName} disabled />
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                  <Field label="Insured DOB" type="date" value={form.insuredDOB} disabled />
                  <Field label="Insured Sex" value={form.insuredSex} disabled />
                </div>
                <Field label="Insured Address" value={form.insuredAddress} disabled />
                <div className="grid grid-cols-12 gap-4">
                  <div className="col-span-12 lg:col-span-6">
                    <Field label="City" value={form.insuredCity} disabled />
                  </div>
                  <div className="col-span-6 lg:col-span-3">
                    <Field label="State" value={form.insuredState} disabled />
                  </div>
                  <div className="col-span-6 lg:col-span-3">
                    <Field
                      label="ZIP"
                      value={form.insuredZip}
                      disabled
                      hint={ZIP_HINT}
                      error={!!form.insuredZip && !isValidZip(form.insuredZip)}
                    />
                  </div>
                </div>
              </div>
            </Section>

            {showDefaults && (
              <Section title="Patient Condition">
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                  <Field label="Employment" value={form.conditionEmployment} disabled />
                  <Field label="Auto Accident" value={form.conditionAuto} disabled />
                  <Field label="Other Accident" value={form.conditionOther} disabled />
                </div>
                <Field label="Policy Group / FECA Number" value={form.policyGroup} disabled />
              </Section>
            )}

            {showDefaults && (
              <Section title="Signatures">
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                  <Field label="Patient Signature" value={form.patientSignature} disabled />
                  <Field label="Insured Signature" value={form.insuredSignature} disabled />
                </div>
              </Section>
            )}

            <Section title="Referring Provider">
              <Field
                label="Referring Provider Name"
                value={form.referringProviderName}
                onChange={update("referringProviderName")}
                disabled={viewMode}
              />
              <Field
                label="Referring Provider NPI"
                value={form.referringProviderNPI}
                onChange={update("referringProviderNPI")}
                disabled={viewMode}
              />
            </Section>

            <Section title="Diagnosis">
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <Field
                  label="Primary Diagnosis ICD-10"
                  value={form.primaryDiagnosis}
                  onChange={update("primaryDiagnosis")}
                  placeholder="e.g. G31.1"
                  disabled={viewMode}
                  required
                />
                <Field
                  label="Secondary Diagnosis ICD-10 (optional)"
                  value={form.secondaryDiagnosis}
                  onChange={update("secondaryDiagnosis")}
                  placeholder="Optional"
                  disabled={viewMode}
                />
              </div>
            </Section>

            <Section title="Service">
              <Field
                label="Date of Service"
                type="date"
                value={form.dateOfService}
                onChange={update("dateOfService")}
                disabled={viewMode}
                required
              />
              <Field
                label="Place of Service"
                value={form.placeOfService}
                onChange={update("placeOfService")}
                options={PLACE_OF_SERVICE_OPTIONS}
                disabled={viewMode}
                required
              />
              {showDefaults && (
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                  <Field label="Diagnosis Pointer" value={form.diagnosisPointer} disabled />
                  <Field label="Units (24G)" value={form.units} disabled />
                </div>
              )}
              <Field
                label="HCPCS / Procedure Code"
                value={form.procedureCode}
                onChange={update("procedureCode")}
                disabled={viewMode}
                required
              />
              <div className="mb-3">
                <div className="typo-strong-125">
                  Charges<span className="text-red-600"> *</span>
                </div>
                <div className={[
                  "input md typo-body-medium w-full flex items-center gap-1",
                  viewMode && "bg-neutral-100 text-neutral-400 border-neutral-200 cursor-not-allowed select-none",
                  !viewMode && !String(form.charges ?? "").trim() && "border-red-500 bg-red-50",
                ].filter(Boolean).join(" ")} style={{ padding: 0 }}>
                  <span className="pl-3 text-neutral-500 select-none">$</span>
                  <input
                    type="number"
                    value={form.charges}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    onChange={(e) => update("charges")(e.target.value)}
                    onBlur={(e) => {
                      const v = parseFloat(e.target.value);
                      if (!isNaN(v)) update("charges")(v.toFixed(2));
                    }}
                    disabled={viewMode}
                    className="flex-1 bg-transparent outline-none pr-3 py-2 typo-body-medium"
                    style={{ border: "none", minWidth: 0 }}
                  />
                </div>
              </div>
              <Field
                label="Rendering Provider First Name"
                value={form.renderingProviderFirstName}
                onChange={update("renderingProviderFirstName")}
                placeholder="First name"
                disabled={viewMode}
                required
              />
              <Field
                label="Rendering Provider Last Name"
                value={form.renderingProviderLastName}
                onChange={update("renderingProviderLastName")}
                placeholder="Last name"
                disabled={viewMode}
                required
              />
              <Field
                label="Rendering Provider NPI"
                value={form.renderingProviderNPI}
                onChange={update("renderingProviderNPI")}
                disabled={viewMode}
                required
              />
            </Section>

            <Section title="Accounting">
              <Field
                label="Federal Tax ID / EIN"
                value={form.federalTaxID}
                onChange={update("federalTaxID")}
                disabled={viewMode}
                required
              />
              <Field
                label="Accept Assignment"
                value={form.acceptAssignment}
                disabled
                hideDefaults={hideDefaults}
              />
              <div className="mb-3">
                <div className="typo-strong-125">Total Charges</div>
                <input
                  type="text"
                  value={totalCharges}
                  readOnly
                  className="input md typo-body-medium w-full bg-neutral-100"
                />
                <div className="typo-normal-125 text-muted mt-1">
                  Auto-calculated from Charges
                </div>
              </div>
              <Field
                label="Physician/Supplier Signature"
                value={form.physicianSignature}
                disabled
                hideDefaults={hideDefaults}
              />
            </Section>

            <Section title="Service Facility">
              <Field
                label="Facility Name"
                value={form.facilityName}
                onChange={update("facilityName")}
                disabled={viewMode}
                required
              />
              <Field
                label="Address"
                value={form.facilityAddress}
                onChange={update("facilityAddress")}
                disabled={viewMode}
                required
              />
              <div className="grid grid-cols-12 gap-4">
                <div className="col-span-12 lg:col-span-6">
                  <Field
                    label="City"
                    value={form.facilityCity}
                    onChange={update("facilityCity")}
                    disabled={viewMode}
                    required
                  />
                </div>
                <div className="col-span-6 lg:col-span-3">
                  <Field
                    label="State"
                    value={form.facilityState}
                    onChange={update("facilityState")}
                    options={STATE_OPTIONS}
                    disabled={viewMode}
                    required
                  />
                </div>
                <div className="col-span-6 lg:col-span-3">
                  <Field
                    label="ZIP"
                    value={form.facilityZip}
                    onChange={(val) => update("facilityZip")(sanitizeZip(val))}
                    disabled={viewMode}
                    required
                    hint={ZIP_HINT}
                    error={!!form.facilityZip && !isValidZip(form.facilityZip)}
                  />
                </div>
              </div>
              <Field
                label="NPI"
                value={form.facilityNPI}
                onChange={update("facilityNPI")}
                disabled={viewMode}
                required
              />
            </Section>

            <Section title="Billing Provider">
              <Field
                label="Type"
                value={form.billingProviderType}
                disabled
                hideDefaults={hideDefaults}
              />
              <Field
                label="Billing Provider Name"
                value={form.billingProviderName}
                onChange={update("billingProviderName")}
                disabled={viewMode}
                required
              />
              <Field
                label="Address"
                value={form.billingProviderAddress}
                onChange={update("billingProviderAddress")}
                disabled={viewMode}
                required
              />
              <div className="grid grid-cols-12 gap-4">
                <div className="col-span-12 lg:col-span-6">
                  <Field
                    label="City"
                    value={form.billingProviderCity}
                    onChange={update("billingProviderCity")}
                    disabled={viewMode}
                    required
                  />
                </div>
                <div className="col-span-6 lg:col-span-3">
                  <Field
                    label="State"
                    value={form.billingProviderState}
                    onChange={update("billingProviderState")}
                    options={STATE_OPTIONS}
                    disabled={viewMode}
                    required
                  />
                </div>
                <div className="col-span-6 lg:col-span-3">
                  <Field
                    label="ZIP"
                    value={form.billingProviderZip}
                    onChange={(val) => update("billingProviderZip")(sanitizeZip(val))}
                    disabled={viewMode}
                    required
                    hint={ZIP_HINT}
                    error={!!form.billingProviderZip && !isValidZip(form.billingProviderZip)}
                  />
                </div>
              </div>
              <Field
                label="Phone"
                value={form.billingProviderPhone}
                onChange={(val) => update("billingProviderPhone")(formatPhone(val))}
                disabled={viewMode}
                required
              />
              <Field
                label="NPI"
                value={form.billingProviderNPI}
                onChange={update("billingProviderNPI")}
                disabled={viewMode}
                required
              />
            </Section>
            {submitErrors && submitErrors.length > 0 && (
              <div className="card-content">
                <div className="rounded border border-red-300 bg-red-50 p-4">
                  <div className="typo-strong-150 text-red-700 mb-2">Submission failed — please correct:</div>
                  <ul className="mb-0 ps-4">
                    {submitErrors.map((desc, i) => (
                      <li key={i} className="typo-normal-150 text-red-700">{desc}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
          <div className="card-content d-flex justify-content-end align-items-center">
            {!viewMode && isIncomplete && (
              <span className="typo-normal-125 text-red-500 me-3">
                {missingCount} required field{missingCount === 1 ? "" : "s"} remaining
              </span>
            )}
            {viewMode ? (
              <Button onClick={onClose} color="grey" muted={true} text="Close" />
            ) : (
              <>
                <Button
                  onClick={onClose}
                  color="grey"
                  muted={true}
                  text="Cancel"
                  className="me-2"
                  disabled={submitting}
                />
                <Button
                  onClick={handleSubmit}
                  color="blue"
                  text={actionLabel}
                  leftIcon={leftIcon}
                  disabled={submitting || (!allowIncomplete && isIncomplete)}
                />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ClaimFormModal;
